import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTrsVerdicts } from '../lib/api';
import { isResolvedPill } from '../lib/trs-pills.mjs';
import VerdictCard, { VERDICT_ORDER } from '../components/TrsVerdictCard.jsx';

// TRS (Trip Request Solver) verdict-review surface.
//
// The crew-positioning solver ("Trip Request Solver") sweeps the held crew-warned quote bucket and
// logs one verdict per quote to cr_crew_solver_log. This page is the reviewer surface for the LATEST
// verdict per quote: Approve/Decline each — an Approve DELIVERS the quote to the broker (CR channel),
// and auto-send is armed for revalidated-VALID quotes only. Server route: GET /api/trs/verdicts.
//
// The verdict card + its tone/symbol/order constants live in the shared components/TrsVerdictCard.jsx
// so the SAME card also renders inline on the trip-detail page (pages/TripDetail.jsx).

// Header summary line: "SEND N · HOLD M · … · Approve delivers · auto-send: revalidated-VALID". These
// are the GLOBAL solver totals (all statuses) — the interactive chips below are filter-scoped instead.
// Shows SEND + HOLD always, plus any other verdict with a non-zero count.
function SummaryLine({ summary }) {
  if (!summary) return null;
  const parts = VERDICT_ORDER
    .filter(k => k === 'SEND' || k === 'HOLD' || (summary[k] || 0) > 0)
    .map(k => `${k} ${summary[k] || 0}`);
  return (
    <div className="text-sm text-slate-600">
      <span className="font-medium">{parts.join(' · ')}</span>
      <span className="text-slate-400"> · Approve delivers · auto-send: revalidated-VALID</span>
    </div>
  );
}

export default function TrsVerdicts() {
  const { data, isLoading, isError, error, refetch } = useTrsVerdicts();
  const verdicts = data?.verdicts || [];
  const summary = data?.summary;

  const [verdictFilter, setVerdictFilter] = useState('ALL');
  const [operatorFilter, setOperatorFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');    // 'newest' | 'verdict'
  // Disposition queue filter — default 'open' so finalized (approved/declined) verdicts leave the
  // working queue (Yves, 2026-07-11). Re-sweeps do NOT re-open: computeDisposition compares the
  // reviewed verdict VALUE, so only a genuine verdict change returns a row to the open queue.
  const [dispFilter, setDispFilter] = useState('open');   // 'open' | 'resolved' | 'all'
  // RESOLVED_PILLS lives in lib/trs-pills.mjs (also covers SQP-297 'Duplicate — already sent').
  const isResolved = v => isResolvedPill(v.disposition?.pill);

  const operators = useMemo(
    () => [...new Set(verdicts.map(v => v.operator_id).filter(Boolean))].sort(),
    [verdicts]
  );

  const dispCounts = useMemo(() => ({
    open: verdicts.filter(v => !isResolved(v)).length,
    resolved: verdicts.filter(isResolved).length,
  }), [verdicts]);

  // The population the verdict chips count over AND the queue draws from: the active disposition
  // (Open/Resolved) + operator + search filters applied, but NOT the verdict filter itself.
  // Counting the chips over THIS — instead of the all-status server `summary` — makes each chip's
  // badge equal exactly what selecting it shows. Fixes "ALREADY_LEGAL 13 badge but an empty Open
  // queue": the badge was the total across every status, while the list is Open-only, and
  // ALREADY_LEGAL/SEND/STALE are almost all Resolved (approved/sent/auto-resolved).
  const scoped = useMemo(() => {
    let list = verdicts;
    if (dispFilter === 'open') list = list.filter(v => !isResolved(v));
    else if (dispFilter === 'resolved') list = list.filter(isResolved);
    if (operatorFilter !== 'ALL') list = list.filter(v => v.operator_id === operatorFilter);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter(v =>
      (v.qid && String(v.qid).toLowerCase().includes(q)) ||
      (v.trip_id && v.trip_id.toLowerCase().includes(q)) ||
      (v.centralized_request_id && v.centralized_request_id.toLowerCase().includes(q))
    );
    return list;
  }, [verdicts, dispFilter, operatorFilter, query]);

  // Verdict-chip badge counts — over `scoped`, so a chip's number always matches the list it yields.
  const verdictCounts = useMemo(() => {
    const c = { ALL: scoped.length };
    for (const v of scoped) c[v.verdict] = (c[v.verdict] || 0) + 1;
    return c;
  }, [scoped]);

  const shown = useMemo(() => {
    let list = verdictFilter === 'ALL' ? scoped : scoped.filter(v => v.verdict === verdictFilter);
    if (sort === 'verdict') {
      list = [...list].sort((a, b) =>
        VERDICT_ORDER.indexOf(a.verdict) - VERDICT_ORDER.indexOf(b.verdict) || (b.id || 0) - (a.id || 0)
      );
    }
    return list;   // server already returns newest-first for the default sort
  }, [scoped, verdictFilter, sort]);

  return (
    <div className="space-y-4">
      {/* Header / explainer */}
      <div className="card p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg font-semibold text-sentinel-navy">TRS — Trip Request Solver</h1>
            <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">
              The crew/ops Trip Request Solver logs a SEND / HOLD verdict per held crew-warned quote —
              re-slotting positioning legs to clear the crew RED chip. Reviewers Approve / Decline each;{' '}
              <span className="font-medium">an Approve delivers the quote to the broker</span> via the CR
              channel, and <span className="font-medium">auto-send is armed for revalidated-VALID quotes
              only</span>. Each row shows the verdict, the reasoning, the candidate trace, and a link to
              the Sentinel quote.
            </p>
          </div>
          <div className="text-right shrink-0">
            <SummaryLine summary={summary} />
            {data && <div className="text-xs text-slate-400 mt-0.5">{data.count} quote{data.count === 1 ? '' : 's'}</div>}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Verdict chips */}
          <div className="flex flex-wrap gap-1">
            {['ALL', ...VERDICT_ORDER].map(k => {
              const count = k === 'ALL' ? (verdictCounts.ALL || 0) : (verdictCounts[k] || 0);
              const active = verdictFilter === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setVerdictFilter(k)}
                  className={clsx(
                    'px-2 py-1 rounded-md text-xs font-medium transition-colors',
                    active ? 'bg-sentinel-navy text-white' : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50'
                  )}
                >
                  {k === 'ALL' ? 'All' : k} <span className="opacity-70">{count}</span>
                </button>
              );
            })}
          </div>
          {/* Disposition queue filter — Open (default) hides finalized reviews */}
          <div className="flex flex-wrap gap-1 border-l border-slate-200 pl-2 ml-1">
            {[['open', `Open ${dispCounts.open}`], ['resolved', `Resolved ${dispCounts.resolved}`], ['all', 'All']].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setDispFilter(k)}
                className={clsx(
                  'px-2 py-1 rounded-md text-xs font-medium transition-colors',
                  dispFilter === k ? 'bg-sentinel-navy text-white' : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50'
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          {/* Operator filter */}
          {operators.length > 0 && (
            <select
              value={operatorFilter}
              onChange={e => setOperatorFilter(e.target.value)}
              className="text-sm border-slate-300 rounded-md px-2 py-1"
            >
              <option value="ALL">All operators</option>
              {operators.map(op => <option key={op} value={op}>{op}</option>)}
            </select>
          )}
          {/* Sort */}
          <select
            value={sort}
            onChange={e => setSort(e.target.value)}
            className="text-sm border-slate-300 rounded-md px-2 py-1"
          >
            <option value="newest">Newest first</option>
            <option value="verdict">By verdict</option>
          </select>
          {/* Search */}
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="qid / trip / CR…"
            className="text-sm border-slate-300 rounded-md px-2 py-1 w-40"
          />
        </div>
      </div>

      {isLoading && <div className="text-slate-500">Loading…</div>}
      {isError && <div className="text-rose-700">Error: {error.message}</div>}

      {!isLoading && !isError && verdicts.length === 0 && (
        <div className="card p-8 text-center text-slate-500">
          <div className="text-2xl mb-1">🛩️</div>
          No solver verdicts logged yet. The Trip Request Solver logs a verdict per held crew-warned
          quote as it sweeps.
        </div>
      )}

      {!isLoading && !isError && verdicts.length > 0 && shown.length === 0 && (
        <div className="card p-6 text-center text-slate-500 text-sm">No verdicts match the current filters.</div>
      )}

      {shown.length > 0 && (
        <div className="space-y-2">
          {shown.map(v => <VerdictCard key={v.id} v={v} onReviewed={refetch} />)}
        </div>
      )}
    </div>
  );
}
